// src/utils/checkAndUnlockPostAssessment.js
//
// Helper that runs after a workbook is marked complete.
// Checks if all the participant's assigned workbooks are now complete.
// If yes, sets postAssessmentUnlocked: true on the user document and
// returns true so the calling component can show the celebration modal.
//
// Returns: { unlocked: boolean, alreadyUnlocked: boolean }
// unlocked === true means THIS save just triggered the unlock (show modal)
// alreadyUnlocked === true means the unlock fired in a previous session (no modal)

import React from 'react';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

export async function checkAndUnlockPostAssessment(userId) {
  try {
    // Read user profile to get assigned day range
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) return { unlocked: false, alreadyUnlocked: false };
    const userData = userSnap.data();

    // If already unlocked previously, do not fire the modal again
    if (userData.postAssessmentUnlocked === true) {
      return { unlocked: false, alreadyUnlocked: true };
    }

    const dayStart = userData.dayRangeStart || 1;
    const dayEnd = userData.dayRangeEnd || 10;

    // Read workbook progress
    const wbRef = doc(db, 'workbooks', userId);
    const wbSnap = await getDoc(wbRef);
    if (!wbSnap.exists()) return { unlocked: false, alreadyUnlocked: false };
    const progress = wbSnap.data().progress || {};

    // Count assigned days that are marked complete
    let completedCount = 0;
    let assignedCount = 0;
    for (let day = dayStart; day <= dayEnd; day++) {
      assignedCount++;
      if (progress[`day${day}`]?.status === 'complete') {
        completedCount++;
      }
    }

    // If every assigned workbook is complete, unlock the post-assessment
    if (completedCount === assignedCount && assignedCount > 0) {
      await updateDoc(userRef, {
        postAssessmentUnlocked: true,
        postAssessmentUnlockedAt: serverTimestamp(),
        postAssessmentUnlockedBy: 'workbook_auto',
      });
      return { unlocked: true, alreadyUnlocked: false };
    }

    return { unlocked: false, alreadyUnlocked: false };
  } catch (err) {
    console.error('checkAndUnlockPostAssessment error:', err);
    return { unlocked: false, alreadyUnlocked: false };
  }
}

// ============================================================
// Celebration modal shown when auto-unlock fires
// ============================================================

export function WorkbookCompletionModal({ isOpen, onClose, onGoToAssessment, onGoToExam }) {
  if (!isOpen) return null;

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
        <div style={modalStyles.headerBar}>
          <div style={modalStyles.headerTag}>Program Milestone</div>
          <div style={modalStyles.headerTitle}>You finished every workbook</div>
        </div>

        <div style={modalStyles.body}>
          <div style={modalStyles.icon}>🎉</div>
          <div style={modalStyles.headline}>Congratulations</div>
          <div style={modalStyles.message}>
            You finished all your workbooks. Your post-training Snapshot and Final Exam are ready when you are.
          </div>

          <div style={modalStyles.actions}>
            <button style={modalStyles.primaryBtn} onClick={onGoToAssessment}>
              Take the Snapshot →
            </button>
            <button style={modalStyles.secondaryBtn} onClick={onGoToExam}>
              Go to Final Exam →
            </button>
          </div>

          <button style={modalStyles.dismissBtn} onClick={onClose}>
            I will come back to this later
          </button>
        </div>
      </div>
    </div>
  );
}

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(27, 42, 74, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
  },
  modal: {
    background: '#FFFFFF',
    maxWidth: '560px',
    width: '100%',
    border: '2px solid #D4A847',
    boxShadow: '0 12px 48px rgba(0, 0, 0, 0.3)',
  },
  headerBar: {
    background: '#1B2A4A',
    padding: '24px 32px',
    textAlign: 'center',
  },
  headerTag: {
    fontSize: '10px',
    fontWeight: '600',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    color: '#D4A847',
    marginBottom: '8px',
  },
  headerTitle: {
    fontFamily: 'Playfair Display, serif',
    fontSize: '22px',
    fontWeight: '700',
    color: '#FFFFFF',
  },
  body: {
    padding: '36px 32px 28px',
    textAlign: 'center',
  },
  icon: {
    fontSize: '56px',
    marginBottom: '12px',
  },
  headline: {
    fontFamily: 'Playfair Display, serif',
    fontSize: '28px',
    fontWeight: '900',
    color: '#1B2A4A',
    marginBottom: '14px',
  },
  message: {
    fontSize: '15px',
    color: '#3D3D3D',
    fontWeight: '400',
    lineHeight: '1.7',
    marginBottom: '28px',
    maxWidth: '440px',
    margin: '0 auto 28px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginBottom: '16px',
  },
  primaryBtn: {
    background: '#1B2A4A',
    color: '#FFFFFF',
    border: 'none',
    padding: '14px 26px',
    fontFamily: 'Playfair Display, serif',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  secondaryBtn: {
    background: '#D4A847',
    color: '#1B2A4A',
    border: 'none',
    padding: '14px 26px',
    fontFamily: 'Playfair Display, serif',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  dismissBtn: {
    background: 'transparent',
    color: '#8A8A8A',
    border: 'none',
    fontSize: '12px',
    cursor: 'pointer',
    textDecoration: 'underline',
    fontFamily: 'DM Sans, sans-serif',
    padding: '8px',
  },
};
